import { api } from '@/config/api';
import type { IndustryValue } from '@/services/business-profile.service';
import type { CrmLabel, LabelKey, PageKey } from '@/lib/crm-pages';

export interface AiAccessSettings {
  billingMode: 'slightor_managed' | 'byok';
  responseMode: 'fast' | 'balanced' | 'premium' | 'custom';
  modelsByMode: Record<string, Record<'fast' | 'balanced' | 'premium', string>>;
}

/** Families with a blueprint — mirrors BLUEPRINT_FAMILIES in backend/src/common/industry/crm-pages.ts. */
export type BlueprintFamilyKey = 'technology' | 'food' | 'real_estate' | 'travel';

/** Platform-owned facts about this workspace, set from the super-admin panel. */
export interface WorkspaceInfo {
  companyName: string;
  /** Assigned by the platform (or picked once at onboarding) — read-only in the CRM. */
  industry: IndustryValue | null;
  industryLocked: boolean;
  country: string | null;
  defaultCurrency: string | null;
  /** Resolved currency every new deal, document and price starts in (set by the platform). */
  currency: string;
  timezone: string;
  /** The industry family's blueprint, or null for the General CRM. */
  family: { key: BlueprintFamilyKey; name: string; version: number } | null;
  /** Pages the blueprint offers. */
  availablePages: Record<PageKey, boolean>;
  /** availablePages minus what the company admin hid. */
  pages: Record<PageKey, boolean>;
  hiddenPages: PageKey[];
  labels: Record<LabelKey, CrmLabel>;
}

export interface MeProfile {
  _id: string; userUid: string; companyUid: string; email: string;
  firstName: string; lastName: string; phone?: string;
  role: 'super_admin' | 'company_admin' | 'company_user';
  status: 'active' | 'invited' | 'suspended';
  lastLoginAt?: string; createdAt: string; updatedAt: string;
  twoFactor?: { enabled: boolean; method?: 'totp' | 'whatsapp' };
}

export interface TeamUser extends MeProfile {}

export interface PlanLimits {
  maxUsers: number; maxContacts: number; maxCampaignsPerMonth: number;
  maxMessagesPerMonth: number; aiCreditsPerMonth: number;
}

export type SeatKey =
  | 'whatsappAccounts' | 'facebookAccounts' | 'googleAdsAccounts'
  | 'tiktokAccounts'   | 'emailAccounts'
  | 'users'            | 'botFlows';

export interface SeatRow {
  key: SeatKey;
  label: string;
  allowed: number;
  perUnitPrice: number;
  subtotal: number;
}

export interface SeatsInfo {
  currency: string;
  usesCustomPricing: boolean;
  baseMonthlyFee: number;
  modules: SeatRow[];
  monthlyTotal: number;
}

export interface UpdateSeatsPayload {
  whatsappAccounts?: number;
  facebookAccounts?: number;
  googleAdsAccounts?: number;
  tiktokAccounts?: number;
  emailAccounts?: number;
  users?: number;
  botFlows?: number;
}

export interface PlanUsage {
  company: { companyUid: string; name: string; slug: string; status: string; expiresAt: string };
  plan: { planId: string; displayName: string; monthlyPriceUsd: number; limits: PlanLimits } | null;
  usage: {
    users: number; contacts: number; groups: number;
    campaignsThisMonth: number; messagesThisMonth: number; aiCreditsThisMonth: number;
    whatsappAccounts: number; facebookAccounts: number; googleAdsAccounts: number;
    tiktokAccounts: number; emailAccounts: number;
  };
  seats: SeatsInfo;
}

export interface IntegrationsSummary {
  whatsapp: {
    accountUid: string; businessName: string; displayPhoneNumber: string; verifiedName: string;
    qualityRating?: string; messagingLimitTier?: string; isActive: boolean; healthFetchedAt?: string;
  } | null;
  facebook: {
    accountUid: string; adAccountId: string; adAccountName: string; currency: string;
    accountStatus?: number; amountSpent?: string; balance?: string;
    pageId?: string; instagramAccountId?: string; isActive: boolean;
    tokenExpiresAt?: string; oauthGrantedAt?: string; tokenRefreshError?: string;
  } | null;
}

export type AiProviderName = 'openai' | 'claude' | 'qwen' | 'gemini' | 'mistral';

export interface AiProviderKey {
  _id: string; provider: AiProviderName; defaultModel: string;
  isDefault: boolean; isActive: boolean; keyMasked: string;
  lastTestedAt?: string; lastTestResult?: 'ok' | 'failed';
}

export interface AiModelEntry {
  id: string;
  label: string;
  tier: 'mini' | 'standard' | 'flagship' | 'fast' | 'pro';
  contextWindow?: number;
  deprecated?: boolean;
}

export interface AiProviderEntry {
  id: AiProviderName;
  label: string;
  consoleUrl?: string;
  available: boolean;
  models: AiModelEntry[];
}

export interface AiModelsResponse {
  providers: AiProviderEntry[];
  default: { provider: AiProviderName; model: string };
}

export interface AuditEntry {
  _id: string; actorUid: string; actorRole: string; action: string;
  targetType: string; targetUid?: string; payload?: Record<string, unknown>;
  ip?: string; userAgent?: string; createdAt: string;
  /** Written by the audit interceptor: a refused request is logged too, with its HTTP status. */
  outcome?: 'succeeded' | 'failed'; statusCode?: number;
}

export interface UpsertAiKeyPayload {
  provider: AiProviderName;
  apiKey: string;
  defaultModel: string;
  isDefault?: boolean;
  isActive?: boolean;
}

export const settingsService = {
  // Profile
  me: async (): Promise<MeProfile> => (await api.get('/slighter_crm/settings/me')).data,
  /** Sidebar modules the platform admin enabled for this company. null = all. */
  enabledModules: async (): Promise<{ enabledModules: string[] | null; isPlatformOwner: boolean }> =>
    (await api.get('/slighter_crm/settings/modules')).data,
  workspace: async (): Promise<WorkspaceInfo> => (await api.get('/slighter_crm/settings/workspace')).data,
  setHiddenPages: async (hiddenPages: PageKey[]): Promise<WorkspaceInfo> =>
    (await api.put('/slighter_crm/settings/workspace/pages', { hiddenPages })).data,
  updateMe: async (p: { firstName?: string; lastName?: string; phone?: string }): Promise<MeProfile> =>
    (await api.patch('/slighter_crm/settings/me', p)).data,
  changePassword: async (p: { currentPassword: string; newPassword: string }) =>
    (await api.post('/slighter_crm/settings/me/change-password', p)).data,

  // Team
  listTeam: async (): Promise<TeamUser[]> => (await api.get('/slighter_crm/settings/team')).data,
  createTeamUser: async (p: { firstName: string; lastName: string; email: string; password: string; role?: 'company_admin' | 'company_user' }) =>
    (await api.post('/slighter_crm/settings/team', p)).data as TeamUser,
  updateTeamUser: async (userUid: string, p: Partial<TeamUser> & { newPassword?: string }) =>
    (await api.patch(`/slighter_crm/settings/team/${userUid}`, p)).data as TeamUser,
  removeTeamUser: async (userUid: string) => { await api.delete(`/slighter_crm/settings/team/${userUid}`); },

  // Plan & usage
  plan: async (): Promise<PlanUsage> => (await api.get('/slighter_crm/settings/plan')).data,

  // Per-module seats — company_admin can update during the testing phase
  updateSeats: async (p: UpdateSeatsPayload): Promise<SeatsInfo> =>
    (await api.patch('/slighter_crm/settings/seats', p)).data,

  // Integrations
  integrations: async (): Promise<IntegrationsSummary> => (await api.get('/slighter_crm/settings/integrations')).data,

  // AI keys (per-tenant)
  listAiKeys: async (): Promise<AiProviderKey[]> => (await api.get('/slighter_crm/settings/ai-providers')).data,
  upsertAiKey: async (p: UpsertAiKeyPayload) => (await api.put('/slighter_crm/settings/ai-providers', p)).data,
  removeAiKey: async (id: string) => { await api.delete(`/slighter_crm/settings/ai-providers/${id}`); },
  testAiKey: async (p: { provider: string; apiKey: string; model: string }) =>
    (await api.post('/slighter_crm/settings/ai-providers/test', p)).data as { ok: true } | { ok: false; error: string },
  testStoredAiKey: async (id: string) =>
    (await api.post(`/slighter_crm/settings/ai-providers/${id}/test`)).data as { ok: true } | { ok: false; error: string },
  revealAiKey: async (id: string): Promise<{ provider: AiProviderName; apiKey: string }> =>
    (await api.get(`/slighter_crm/settings/ai-providers/${id}/reveal`)).data,

  // Activity
  activity: async (q: { actor?: string; action?: string; page?: number; limit?: number } = {}) =>
    (await api.get('/slighter_crm/settings/activity', { params: q })).data as { items: AuditEntry[]; total: number; page: number; limit: number },

  // Enhance a user-written prompt using the company default AI model.
  enhancePrompt: async (p: { prompt: string; context?: string; provider?: string; model?: string }): Promise<{ enhanced: string }> =>
    (await api.post('/slighter_crm/settings/ai/enhance-prompt', p)).data,

  // AI model catalogue + company default
  listAiModels: async (): Promise<AiModelsResponse> =>
    (await api.get('/slighter_crm/settings/ai/models')).data,
  getAiAccess: async (): Promise<AiAccessSettings> => (await api.get('/slighter_crm/settings/ai/access')).data,
  setAiAccess: async (input: Pick<AiAccessSettings, 'billingMode' | 'responseMode'>): Promise<AiAccessSettings> => (await api.put('/slighter_crm/settings/ai/access', input)).data,
  getAiDefault: async (): Promise<{ provider: AiProviderName; model: string } | null> =>
    (await api.get('/slighter_crm/settings/ai/default')).data,
  setAiDefault: async (p: { provider: AiProviderName; model: string }): Promise<{ provider: AiProviderName; model: string }> =>
    (await api.put('/slighter_crm/settings/ai/default', p)).data,

  // WhatsApp AI fallback — conversational reply when no scripted flow matches
  getWhatsappAiFallback: async (): Promise<{ enabled: boolean; model: string | null; maxTurns: number }> =>
    (await api.get('/slighter_crm/settings/ai/whatsapp-fallback')).data,
  setWhatsappAiFallback: async (
    p: { enabled?: boolean; model?: string | null; maxTurns?: number },
  ): Promise<{ enabled: boolean; model: string | null; maxTurns: number }> =>
    (await api.put('/slighter_crm/settings/ai/whatsapp-fallback', p)).data,

  // ElevenLabs voiceover key + voice (AI Studio)
  getElevenLabs: async (): Promise<ElevenLabsSettings> =>
    (await api.get('/slighter_crm/settings/ai/elevenlabs')).data,
  setElevenLabs: async (p: {
    apiKey?: string | null;
    voiceId?: string | null;
    voices?: SavedVoice[] | null;
  }): Promise<ElevenLabsSettings> =>
    (await api.put('/slighter_crm/settings/ai/elevenlabs', p)).data,
  /** Browse the company's full ElevenLabs voice library (for the picker). */
  listElevenLabsVoices: async (): Promise<ElevenLabsVoice[]> =>
    (await api.get('/slighter_crm/settings/ai/elevenlabs/voices')).data,
  testElevenLabsKey: async (apiKey: string): Promise<{ ok: boolean; error?: string }> =>
    (await api.post('/slighter_crm/settings/ai/elevenlabs/test', { apiKey })).data,

  // AI usage history
  getAiUsage: async (days = 30): Promise<AiUsageResponse> =>
    (await api.get('/slighter_crm/settings/ai/usage', { params: { days } })).data,

  // AI cost caps (daily + monthly)
  getAiCostCaps: async (): Promise<AiCostCapsResponse> =>
    (await api.get('/slighter_crm/settings/ai/cost-caps')).data,
  setAiCostCaps: async (
    p: { daily?: number | null; monthly?: number | null },
  ): Promise<AiCostCapsResponse> =>
    (await api.put('/slighter_crm/settings/ai/cost-caps', p)).data,

  // Per-language provider routing for the WhatsApp AI fallback
  getLanguagePreferences: async (): Promise<LanguagePreferencesMap> =>
    (await api.get('/slighter_crm/settings/ai/language-preferences')).data,
  setLanguagePreferences: async (preferences: LanguagePreferencesMap): Promise<LanguagePreferencesMap> =>
    (await api.put('/slighter_crm/settings/ai/language-preferences', { preferences })).data,

  // Localisation — country / currency / VAT defaults
  getLocalization: async (): Promise<LocalizationSettings> =>
    (await api.get('/slighter_crm/settings/localization')).data,
  setLocalization: async (patch: Partial<LocalizationSettings>): Promise<LocalizationSettings> =>
    (await api.put('/slighter_crm/settings/localization', patch)).data,
  listCountries: async (): Promise<CountryEntry[]> =>
    (await api.get('/slighter_crm/settings/localization/countries')).data,

  // Lead routing — round-robin auto-assignment of new leads
  getLeadRouting: async (): Promise<LeadRoutingSettings> =>
    (await api.get('/slighter_crm/settings/lead-routing')).data,
  setLeadRouting: async (patch: Partial<LeadRoutingSettings>): Promise<LeadRoutingSettings> =>
    (await api.put('/slighter_crm/settings/lead-routing', patch)).data,
};

export interface LeadRoutingSettings {
  leadAutoAssignEnabled: boolean;
}

export interface LocalizationSettings {
  country: string | null;
  countryLabel?: string | null;
  defaultCurrency: string | null;
  /** Resolved currency new records use — set by the platform, read-only here. */
  currency?: string;
  timezone?: string;
  defaultVatRatePct: number | null;
  defaultVatInclusive: boolean;
  /** The country's standard VAT, shown when the company hasn't set its own. */
  countryVatRatePct?: number | null;
}

export interface CountryEntry {
  code: string;
  label: string;
  currency: string;
  vatRatePct: number;
}

export type LanguagePreferencesMap = Record<string, { provider: AiProviderName; model?: string }>;

export interface SavedVoice {
  voiceId: string;
  name: string;
  accent?: string;
  previewUrl?: string;
}

export interface ElevenLabsVoice {
  voiceId: string;
  name: string;
  accent?: string;
  gender?: string;
  description?: string;
  previewUrl?: string;
}

export interface ElevenLabsSettings {
  configured: boolean;
  keyMasked: string | null;
  voiceId: string | null;
  voices: SavedVoice[];
}

export interface AiCostCapsResponse {
  daily: { capUsd: number | null; spentUsd: number; resetAt: string };
  monthly: { capUsd: number | null; spentUsd: number; resetAt: string };
}

export interface AiUsageRow {
  createdAt: string;
  provider: string;
  model: string;
  feature: string;
  tokensIn: number;
  tokensOut: number;
  costUsd: number;
  creditsCharged: number;
  keyScope: 'platform' | 'company' | 'platform-env';
}

export interface AiUsageResponse {
  days: number;
  recent: AiUsageRow[];
  summary: {
    totalCalls: number;
    totalTokens: number;
    totalCostUsd: number;
    totalCredits: number;
    byProvider: Array<{ provider: string; calls: number; tokens: number; costUsd: number }>;
  };
}
